import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { useEffect, useState } from 'react'
import { auth } from './lib/supabase'
import LoadingSpinner from './components/LoadingSpinner'
import PageLoader from './components/PageLoader'
import { initLoadingInterceptor, loadingState } from './lib/loadingInterceptor'
import { initConsent } from './utils/consentManager'

// Layouts
import PublicLayout from './layouts/PublicLayout'
import AdminLayout from './layouts/AdminLayout'
import ProtectedPublicRoute from './components/ProtectedPublicRoute'

// Public Pages
import Home from './pages/Home'
import Marcas from './pages/Marcas'
import MarcaDetalle from './pages/MarcaDetalle'
import Tiendas from './pages/Tiendas'
import TiendaDetalle from './pages/TiendaDetalle'
import Eventos from './pages/Eventos'
import EventoDetalle from './pages/EventoDetalle'
import Contacto from './pages/Contacto'
import Legal from './pages/Legal'
import LibroReclamaciones from './pages/LibroReclamaciones'
import DerechosARCO from './pages/DerechosARCO'
import Ubicanos from './pages/Ubicanos'
import TrabajaConNosotros from './pages/TrabajaConNosotros'
import Login from './pages/Login'
import ResetPassword from './pages/ResetPassword'

// API Endpoints
import SiteApi from './pages/api/SiteApi'
import BrandsApi from './pages/api/BrandsApi'
import EventsApi from './pages/api/EventsApi'
import Sitemap from './pages/api/Sitemap'

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard'
import AdminUsuario from './pages/admin/Usuario'
import AdminMarcas from './pages/admin/Marcas'
import AdminEventos from './pages/admin/Eventos'
import AdminWebsite from './pages/admin/Website'
import AdminLegal from './pages/admin/Legal'
import AdminContactos from './pages/admin/Contactos'
import AdminReclamos from './pages/admin/Reclamos'
import AdminCRM from './pages/admin/CRM'
import AdminComentarios from './pages/admin/Comentarios'
import AdminFAQs from './pages/admin/FAQs'
import AdminSiteConfig from './pages/admin/SiteConfig'
import Descuentos from './pages/Descuentos'
import DescuentoDetalle from './pages/DescuentoDetalle'
import AdminDescuentos from './pages/admin/Descuentos'

function ProtectedRoute({ children }) {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    auth.getSession().then(({ session }) => {
      setSession(session)
      setLoading(false)
    })

    const { data: { subscription } } = auth.onAuthStateChange((_event, session) => {
      setSession(session)
    })

    return () => subscription.unsubscribe()
  }, [])

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">
      <p className="text-gray-600">Cargando…</p>
    </div>
  }

  if (!session) {
    return <Navigate to="/login" replace />
  }

  return children
}

function ScrollToTop() {
  const location = useLocation()
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [location.pathname])
  return null
}

function RouteAwareInitialLoader({ isLoading }) {
  const location = useLocation()
  const isAdmin = location.pathname.startsWith('/admin')
  if (isAdmin) return null
  return <PageLoader isLoading={isLoading} />
}

function PageTransition({ children }) {
  const location = useLocation()
  const [isLoading, setIsLoading] = useState(false)
  const [requestCount, setRequestCount] = useState(0)
  const [hasMinTimePassed, setHasMinTimePassed] = useState(false)

  useEffect(() => {
    // Subscribe to request count changes
    const unsubscribe = loadingState.subscribe((count) => {
      setRequestCount(count)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    // Start loading on route change
    setIsLoading(true)
    setHasMinTimePassed(false)
    
    // Minimum display time
    const minTimer = setTimeout(() => {
      setHasMinTimePassed(true)
    }, 500)

    // Maximum timeout (force close even if requests pending)
    const maxTimer = setTimeout(() => {
      setIsLoading(false)
    }, 2500) // Increased to 2.5s to allow more time for data

    return () => {
      clearTimeout(minTimer)
      clearTimeout(maxTimer)
    }
  }, [location.pathname])

  // Close when BOTH conditions met: min time passed AND no requests
  useEffect(() => {
    if (hasMinTimePassed && requestCount === 0 && isLoading) {
      // Small delay to ensure DOM is fully updated
      const closeTimer = setTimeout(() => {
        setIsLoading(false)
      }, 300)
      return () => clearTimeout(closeTimer)
    }
  }, [hasMinTimePassed, requestCount, isLoading])

  // Exclude loader for admin routes
  const isAdmin = location.pathname.startsWith('/admin')

  return (
    <>
      {!isAdmin && <PageLoader isLoading={isLoading} />}
      {children}
    </>
  )
}

function App() {
  const [initialLoading, setInitialLoading] = useState(true)
  const [requestCount, setRequestCount] = useState(0)
  const [hasMinTimePassed, setHasMinTimePassed] = useState(false)

  useEffect(() => {
    // Initialize interceptor
    initLoadingInterceptor()
    
    // Subscribe to request count
    const unsubscribe = loadingState.subscribe((count) => {
      setRequestCount(count)
    })

    // Initial load minimum time
    const minTimer = setTimeout(() => {
      setHasMinTimePassed(true)
    }, 800) // Increased min time for initial load

    // Maximum initial load timeout
    const maxTimer = setTimeout(() => {
      setInitialLoading(false)
    }, 3000) // Increased to 3s for initial load

    return () => {
      clearTimeout(minTimer)
      clearTimeout(maxTimer)
      unsubscribe()
    }
  }, [])

  // Close initial load when BOTH: min time passed AND no requests
  useEffect(() => {
    if (hasMinTimePassed && requestCount === 0 && initialLoading) {
      const timer = setTimeout(() => {
        setInitialLoading(false)
      }, 400) // Extra buffer to ensure content is rendered
      return () => clearTimeout(timer)
    }
  }, [hasMinTimePassed, requestCount, initialLoading])

  return (
    <HelmetProvider>
      <GoogleReCaptchaProvider reCaptchaKey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}>
        <BrowserRouter>
          <RouteAwareInitialLoader isLoading={initialLoading} />
          <ScrollToTop />
          <PageTransition>
            <Routes>
          {/* Public Routes */}
          <Route path="/" element={<PublicLayout />}>
            <Route index element={<Home />} />
            <Route path="marcas" element={<Marcas />} />
            <Route path="marcas/:id" element={<MarcaDetalle />} />
            <Route path="tiendas2" element={<Tiendas />} />
            <Route path="tiendas2/:id" element={<TiendaDetalle />} />
            <Route path="eventos" element={<Eventos />} />
            <Route path="eventos/:id" element={<EventoDetalle />} />
            <Route path="contacto" element={<Contacto />} />
            <Route path="legal" element={<Legal />} />
            <Route path="libro-reclamaciones" element={
              <ProtectedPublicRoute pageName="libro_reclamaciones">
                <LibroReclamaciones />
              </ProtectedPublicRoute>
            } />
            <Route path="derechos-arco" element={
              <ProtectedPublicRoute pageName="derechos_arco">
                <DerechosARCO />
              </ProtectedPublicRoute>
            } />
            <Route path="ubicanos" element={<Ubicanos />} />
            <Route path="trabaja" element={<TrabajaConNosotros />} />
            <Route path="/descuentos" element={<Descuentos />} />
            <Route path="/descuentos/:id" element={<DescuentoDetalle />} />
          </Route>

          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* API Routes (LLM-friendly JSON endpoints) */}
          <Route path="/api/site.json" element={<SiteApi />} />
          <Route path="/api/brands.json" element={<BrandsApi />} />
          <Route path="/api/events.json" element={<EventsApi />} />
          <Route path="/sitemap.xml" element={<Sitemap />} />

          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }>
            <Route index element={<AdminDashboard />} />
            <Route path="usuario" element={<AdminUsuario />} />
            <Route path="marcas" element={<AdminMarcas />} />
            <Route path="eventos" element={<AdminEventos />} />
            <Route path="website" element={<AdminWebsite />} />
            <Route path="legal" element={<AdminLegal />} />
            <Route path="contactos" element={<AdminContactos />} />
            <Route path="reclamos" element={<AdminReclamos />} />
            <Route path="crm" element={<AdminCRM />} />
            <Route path="comentarios" element={<AdminComentarios />} />
            <Route path="faqs" element={<AdminFAQs />} />
            <Route path="site-config" element={<AdminSiteConfig />} />
            <Route path="descuentos" element={<AdminDescuentos />} />
          </Route>
          </Routes>
        </PageTransition>
      </BrowserRouter>
      </GoogleReCaptchaProvider>
    </HelmetProvider>
  )
}

export default App
